import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { PencilIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Input } from "@components/inputs/Input/Input";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useEditCustomTransactionCategoryMutation } from "@services/budget/mutations";
import { isNullOrUndefined } from "@utils/common";
import {
  useEditBudgetTransactionCategorySchema,
  type TEditBudgetTransactionCategorySchema,
} from "./useEditBudgetTransactionCategorySchema";

interface IEditBudgetTransactionCategoryModalProps extends IUseModalProps {
  categoryName: string;
  transactionCategoryId: number;
}

export const EditBudgetTransactionCategoryModal: React.FC<
  IEditBudgetTransactionCategoryModalProps
> = (props) => {
  const { categoryName, transactionCategoryId, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const schema = useEditBudgetTransactionCategorySchema();

  const { control, handleSubmit, setError } =
    useForm<TEditBudgetTransactionCategorySchema>({
      resolver: zodResolver(schema),
    });

  const { toastApiError } = useApiErrorUtils(setError);
  const { mutateAsync, isPending } = useEditCustomTransactionCategoryMutation();

  const onSubmit: SubmitHandler<TEditBudgetTransactionCategorySchema> = async (
    data,
  ) => {
    const response = await mutateAsync({
      categoryName: data.name,
      transactionCategoryId: transactionCategoryId,
    }).catch(toastApiError);

    if (!isNullOrUndefined(response)) {
      toast.success(
        t("budget.editBudgetTransactionCategoryModal.toast.success"),
      );
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: PencilIcon,
      }}
      title={t("budget.editBudgetTransactionCategoryModal.title")}
      subtitle={t("budget.editBudgetTransactionCategoryModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" className="w-full" onClick={modal.close}>
            {t("budget.editBudgetTransactionCategoryModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("budget.editBudgetTransactionCategoryModal.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          disabled
          label={t("budget.editBudgetTransactionCategoryModal.oldName.label")}
          value={categoryName}
        />
        <FormInput
          control={control}
          name="name"
          label={t("budget.editBudgetTransactionCategoryModal.newName.label")}
          placeholder={t(
            "budget.editBudgetTransactionCategoryModal.newName.placeholder",
          )}
        />
      </div>
    </Modal>
  );
};
